import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useStomp } from '../../contexts/StompContext';
import {
  Spinner,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Button,
  Badge
} from '@heroui/react';
import { LogOut, User, Settings, MessageCircle, Menu } from 'lucide-react';
import Timeline from '../Timeline/Timeline';
import LoginPage from '../Auth/LoginPage';
import PasswordResetPage from '../Auth/PasswordResetPage';
import MessagingApp from '../Messaging/MessagingApp';
import Profile from '../Profile/Profile';
import Navigation from '../Navigation/Navigation';
import NotificationDropdown from '../Notifications/NotificationDropdown';
import VisibilitySelector from './VisibilitySelector';
import MapView from '../Map/MapView';
import Members from '../Members/Members';
import ugovLogo from '../../assets/logo.png';
// Simple PostView component
const PostView = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`/api/v1/posts/${postId}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          console.log('PostView: API response:', data);
          setPost(data.posts); // Use posts envelope
        } else {
          setError(`Post not found (${response.status})`);
        }
      } catch (err) {
        setError('Failed to load post');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [postId]);

  if (loading) return <div className="text-center py-8">Loading post...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/')}
        className="mb-4 text-blue-600 hover:text-blue-800"
      >
        ← Back to Timeline
      </button>
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-xl font-bold mb-4">{post?.title || 'Untitled Post'}</h1>
        <div className="text-gray-800 whitespace-pre-wrap">{post?.body}</div>
        <div className="mt-4 text-sm text-gray-500">
          By {post?.user?.username} • {new Date(post?.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

const Layout = () => {
  const { isAuthenticated, loading, user, logout } = useAuth();
  const { isConnected, connectionStatus, addMessageHandler, subscribeToTimelineTopic } = useStomp();
  const navigate = useNavigate();

  const [visibilityParams, setVisibilityParams] = useState({});

  const handleVisibilityChange = (newLevel, selectedOption) => {
    // Build query parameters based on visibility level and selected option
    const params = {};
    if (selectedOption) {
      if (selectedOption.area_id) {
        params.area_id = selectedOption.area_id;
      } else if (selectedOption.city_id) {
        params.city_id = selectedOption.city_id;
      } else if (selectedOption.state_id) {
        params.state_id = selectedOption.state_id;
      }
    }
    setVisibilityParams(params);
    setPosts([]); // Clear posts to trigger refetch
    
    // Update STOMP subscription to match new visibility level
    if (subscribeToTimelineTopic) {
      subscribeToTimelineTopic(params);
    }
  };

  // Check for password reset token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const resetPasswordToken = urlParams.get('reset_password_token');

  const [showMessaging, setShowMessaging] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [showNavigation, setShowNavigation] = useState(true);
  const [currentView, setCurrentView] = useState('timeline'); // timeline, map, members
  const [currentCategory, setCurrentCategory] = useState(null);
  const [posts, setPosts] = useState([]);
  const [targetConversation, setTargetConversation] = useState(null);

  // Handle password reset success
  const handlePasswordResetSuccess = () => {
    // Clear the token from URL
    window.history.replaceState({}, document.title, window.location.pathname);
    // Show login page
    navigate('/');
  };

  // Debug logging for Layout component
  useEffect(() => {
    console.log('Layout: Auth state changed - isAuthenticated:', isAuthenticated, 'loading:', loading, 'user:', user?.username || 'none');
  }, [isAuthenticated, loading, user]);

  // Handle chat messages for unread count
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const handleChatMessage = (message, topic) => {
      console.log('Layout: Received chat message', message);

      // Only count as unread if messaging app is not open and message is from another user
      if (!showMessaging && message.message && message.user && message.user.username !== user.username) {
        console.log('Layout: Incrementing unread count for message from:', message.user.username);
        setUnreadMessageCount(prev => prev + 1);
      }
    };

    // Subscribe to chat messages
    const cleanup = addMessageHandler('chat', handleChatMessage);

    return cleanup;
  }, [isAuthenticated, user, addMessageHandler]);

  const handleGoHome = () => {
    setShowMessaging(false);
    setShowProfile(false);
    setCurrentView('timeline');
    setTargetConversation(null);
  };

  const handleLogout = async () => {
    console.log('Layout: Logout clicked');
    await logout();
  };

  const handleMessagingToggle = async () => {
    setShowMessaging(!showMessaging);

    // Refresh unread count when opening/closing messaging
    if (!showMessaging) {
      // Opening messaging - refresh count and clear if needed
      await fetchUnreadCount();
    } else {
      // Closing messaging - refresh count
      await fetchUnreadCount();
    }
  };

  const handleProfileToggle = () => {
    setShowProfile(!showProfile);
  };

  const handleMessagingClose = async () => {
    setShowMessaging(false);
    setTargetConversation(null); // Clear target conversation when closing

    // Refresh unread count when closing messaging app
    await fetchUnreadCount();
  };

  const handleOpenMessagingWithConversation = (conversation) => {
    setTargetConversation(conversation);
    setShowMessaging(true);
    setUnreadMessageCount(0);
  };

  // Function to mark messages as read and update unread count
  // Make functions available globally for messaging components
  useEffect(() => {
    window.openMessagingWithConversation = handleOpenMessagingWithConversation;
    window.updateUnreadCount = updateUnreadCount;
    window.fetchUnreadCount = fetchUnreadCount;

    return () => {
      delete window.openMessagingWithConversation;
      delete window.updateUnreadCount;
      delete window.fetchUnreadCount;
    };
  }, []);

  // Fetch initial unread message count
  const fetchUnreadCount = async () => {
    try {
      console.log('Layout: Fetching unread message count from conversations...');
      const response = await fetch('/api/v1/conversations?per_page=100', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Layout: Received conversations data:', data);

        // Count conversations with has_unread: true
        const unreadConversations = data.conversations?.filter(conv => conv.has_unread === true) || [];
        const count = unreadConversations.length;

        console.log('Layout: Found unread conversations:', unreadConversations.map(c => ({
          id: c._id,
          has_unread: c.has_unread,
          latest_message: c.latest_message?.body?.substring(0, 30)
        })));
        console.log('Layout: Total unread count:', count);

        setUnreadMessageCount(count);

        // Update document title if there are unread messages
        if (count > 0) {
          document.title = `ugov (${count})`;
        } else {
          document.title = 'ugov';
        }
      } else {
        console.error('Layout: Failed to fetch conversations:', response.status);
      }
    } catch (error) {
      console.error('Layout: Error fetching unread count:', error);
    }
  };

  // Update unread count (can be called from messaging components)
  const updateUnreadCount = (newCount) => {
    console.log('Layout: Updating unread count to:', newCount);
    setUnreadMessageCount(newCount);

    // Update document title
    if (newCount > 0) {
      document.title = `ugov (${newCount})`;
    } else {
      document.title = 'ugov';
    }
  };

  // Fetch unread count when user logs in and set up real-time updates
  useEffect(() => {
    if (isAuthenticated && user) {
      // Fetch initial count
      fetchUnreadCount();

      // Set up STOMP handler for real-time message updates
      const handleChatUpdate = (message, topic) => {
        console.log('Layout: Received chat update via STOMP:', message);

        // Update unread count when new messages arrive
        if (message.message) {
          fetchUnreadCount();
        }
      };

      // Subscribe to chat updates
      const cleanup = addMessageHandler('chat', handleChatUpdate);

      return cleanup;
    } else {
      setUnreadMessageCount(0);
      document.title = 'ugov';
    }
  }, [isAuthenticated, user, addMessageHandler]);

  const handleProfileClose = () => {
    setShowProfile(false);
  };

  const handleDebugToggle = (event) => {
    // Only toggle if shift key is held down
    if (event.shiftKey) {
      setShowDebugInfo(!showDebugInfo);
    }
  };

  const handleCategoryChange = (category) => {
    setCurrentCategory(category);
    setCurrentView('timeline');
  };

  const handleShowMap = () => {
    setCurrentView('map');
  };

  const handleShowMembers = () => {
    setCurrentView('members');
  };

  const handleCloseSpecialView = () => {
    setCurrentView('timeline');
  };

  const handlePostsUpdate = (newPosts) => {
    setPosts(newPosts);
  };

  console.log('Layout: Rendering with isAuthenticated:', isAuthenticated, 'loading:', loading);

  if (loading) {
    console.log('Layout: Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-gray-600">Verificando autenticação...</p>
          <p className="mt-2 text-xs text-gray-400">
            isAuthenticated: {isAuthenticated ? 'true' : 'false'}, user: {user?.username || 'none'}
          </p>
        </div>
      </div>
    );
  }

  // Show password reset page if token is present
  if (resetPasswordToken) {
    return <PasswordResetPage token={resetPasswordToken} onSuccess={handlePasswordResetSuccess} />;
  }

  if (!isAuthenticated) {
    console.log('Layout: User not authenticated, showing LoginPage');
    return <LoginPage />;
  }

  console.log('Layout: User authenticated, showing Timeline');
  console.log(" USER AREA: " + JSON.stringify(user));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="flex items-center justify-between max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={handleGoHome}>
              <img
                src={ugovLogo}
                alt="ugov"
                className="h-8 w-auto"
              />
              <span className="font-bold text-xl" style={{ color: '#01aef0' }}>ugov</span>
            </div>
            <div className="flex items-center space-x-2">
              <Chip
                size="sm"
                color={isConnected ? 'success' : 'warning'}
                variant="flat"
                className="text-xs"
              >
                {isConnected ? 'Online' : 'Reconectando...'}
              </Chip>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Notifications */}
            <NotificationDropdown />

            {/* Messaging Button */}
            <Badge
              content={unreadMessageCount > 0 ? unreadMessageCount : null}
              color="danger"
              size="sm"
              isInvisible={unreadMessageCount === 0}
            >
              <Button
                isIconOnly
                variant="light"
                onPress={handleMessagingToggle}
                className="text-gray-600 hover:text-primary"
                title="Conversas"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
            </Badge>

            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-gray-900">
                {user?.username || 'Usuário'}
              </div>
              <VisibilitySelector 
                user={user} 
                onVisibilityChange={handleVisibilityChange}
              />
            </div>

            {/* Mobile visibility selector */}
            <div className="sm:hidden">
              <VisibilitySelector 
                user={user} 
                onVisibilityChange={handleVisibilityChange}
              />
            </div>

            {/* User Menu */}
            <Dropdown
              placement="bottom-end"
              classNames={{
                base: "before:bg-default-200",
                content: "bg-white shadow-2xl border border-gray-200 rounded-lg p-0"
              }}
            >
              <DropdownTrigger>
                <Button
                  variant="light"
                  className="p-0 min-w-0 h-auto"
                >
                  <Avatar
                    size="sm"
                    name={user?.username || 'U'}
                    src={user?.avatar}
                    className="cursor-pointer"
                  />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label="User Actions"
                variant="flat"
                classNames={{
                  base: "bg-white",
                  list: "bg-white"
                }}
              >
                <DropdownItem key="profile" className="h-14 gap-2">
                  <p className="font-semibold">Conectado como</p>
                  <p className="font-semibold">{user?.username}</p>
                </DropdownItem>
                <DropdownItem
                  key="messages"
                  startContent={<MessageCircle className="w-4 h-4" />}
                  onPress={handleMessagingToggle}
                >
                  Conversas
                  {unreadMessageCount > 0 && (
                    <Chip size="sm" color="danger" variant="solid" className="ml-2">
                      {unreadMessageCount}
                    </Chip>
                  )}
                </DropdownItem>
                <DropdownItem
                  key="settings"
                  startContent={<Settings className="w-4 h-4" />}
                >
                  Configurações
                </DropdownItem>
                <DropdownItem
                  key="profile_page"
                  startContent={<User className="w-4 h-4" />}
                  onPress={handleProfileToggle}
                >
                  Meu Perfil
                </DropdownItem>
                <DropdownItem
                  key="logout"
                  color="danger"
                  startContent={<LogOut className="w-4 h-4" />}
                  onPress={handleLogout}
                >
                  Sair
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Debug Info - Only in development, collapsible with shift+click */}
      {process.env.NODE_ENV === 'development' && (
        <div
          className={`bg-gray-100 border-b cursor-pointer transition-all duration-200 ${showDebugInfo ? 'h-auto' : 'h-5'} overflow-hidden`}
          onClick={handleDebugToggle}
          title="Hold Shift + Click to toggle debug info"
        >
          <div className="max-w-4xl mx-auto px-6 py-2">
            {showDebugInfo ? (
              <div className="text-xs text-gray-600 grid grid-cols-2 md:grid-cols-6 gap-2">
                <div><strong>Auth:</strong> {isAuthenticated ? 'Yes' : 'No'}</div>
                <div><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</div>
                <div><strong>User:</strong> {user?.username || 'None'}</div>
                <div><strong>Area ID:</strong> {user?.area_id || 'None'}</div>
                <div><strong>Stomp Key:</strong> {user?.stomp_key ? 'Present' : 'None'}</div>
                <div><strong>STOMP:</strong> {connectionStatus}</div>
              </div>
            ) : (
              <div className="h-1 rounded-full opacity-50"></div>
            )}
          </div>
        </div>
      )}

      {/* Messaging App Overlay */}
      {showMessaging && (
        <MessagingApp
          user={user}
          onClose={handleMessagingClose}
          onHome={handleMessagingClose}
          targetConversation={targetConversation}
        />
      )}

      {/* Profile Page Overlay */}
      {showProfile && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <Profile
            userId={user._id}
            onClose={handleProfileClose}
          />
        </div>
      )}

      {/* Mobile Navigation Toggle */}
      <div className="lg:hidden fixed bottom-4 right-4 z-40">
        <Button
          isIconOnly
          color="primary"
          className="shadow-lg"
          onPress={() => setShowNavigation(!showNavigation)}
        >
          <Menu className="w-5 h-5" />
        </Button>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex gap-6">
          {/* Navigation Sidebar - Adjacent to Timeline */}
          {showNavigation && currentView === 'timeline' && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <Navigation
                onCategoryChange={handleCategoryChange}
                onShowMap={handleShowMap}
                onShowMembers={handleShowMembers}
                currentCategory={currentCategory}
              />
            </div>
          )}

          {/* Timeline Content Area */}
          <div className="flex-1 min-w-0">
            <Routes>
              <Route path="/" element={
                <Timeline
                  category={currentCategory}
                  onPostsUpdate={handlePostsUpdate}
                  visibilityParams={visibilityParams}
                />
              } />
              <Route path="/posts/:postId" element={<PostView />} />
            </Routes>
          </div>
        </div>
      </div>

      {/* Full Screen Views */}
      {currentView === 'map' && (
        <MapView
          onClose={handleCloseSpecialView}
          category={currentCategory}
          posts={posts}
        />
      )}

      {currentView === 'members' && (
        <Members
          onClose={handleCloseSpecialView}
        />
      )}

      {/* Mobile Navigation Overlay */}
      {showNavigation && currentView === 'timeline' && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-30" onClick={() => setShowNavigation(false)}>
          <div className="bg-white w-64 h-full p-6" onClick={(e) => e.stopPropagation()}>
            <Navigation
              onCategoryChange={(category) => {
                handleCategoryChange(category);
                setShowNavigation(false);
              }}
              onShowMap={() => {
                handleShowMap();
                setShowNavigation(false);
              }}
              onShowMembers={() => {
                handleShowMembers();
                setShowNavigation(false);
              }}
              currentCategory={currentCategory}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
